import React, { useState } from "react";
import { ConstructorEventUpdateSQLStatus } from "../../../../Event/event";

const styles = {
  body: {
    fontFamily: "'Comic Sans MS', cursive, sans-serif",
    background: "#fff8e1",
    color: "#5d4037",
    padding: 20,
    textAlign: "center",
    minHeight: "100vh",
    boxSizing: "border-box",
  },
  heading: { color: "#6d4c41", marginBottom: 10 },
  instructions: { fontSize: "1.2rem", marginBottom: 15 },
  numberContainer: {
    display: "flex",
    justifyContent: "center",
    gap: 15,
    marginBottom: 25,
    flexWrap: "wrap",
    maxWidth: 480,
    marginLeft: "auto",
    marginRight: "auto",
  },
  numberBox: {
    backgroundColor: "#a1887f",
    color: "white",
    fontWeight: "bold",
    fontSize: "1.8rem",
    padding: "15px 22px",
    borderRadius: 10,
    cursor: "grab",
    userSelect: "none",
    border: "3px solid transparent",
    transition: "border-color 0.3s, background-color 0.3s",
    minWidth: 60,
    textAlign: "center",
  },
  dragging: { opacity: 0.5, cursor: "grabbing" },
  highlight: { borderColor: "#f57c00", backgroundColor: "#ffcc80", color: "#bf360c" },
  answerSection: { marginTop: 20, fontSize: "1.2rem" },
  input: {
    width: "20%",
    margin: "0 auto",
    marginTop: "10px",
    fontSize: "1.3rem",
    padding: "6px 10px",
    borderRadius: 8,
    border: "2px solid #a1887f",
    textAlign: "center",
    maxWidth: "90vw",
  },
  button: {
    marginTop: 15,
    backgroundColor: "#6d4c41",
    color: "white",
    border: "none",
    padding: "12px 28px",
    fontSize: "1.2rem",
    borderRadius: 12,
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  buttonHover: { backgroundColor: "#4e342e" },
  feedback: { marginTop: 18, fontSize: "1.25rem", minHeight: 40, fontWeight: "bold" },
  explanation: { marginTop: 15, fontStyle: "italic", maxWidth: 500, marginLeft: "auto", marginRight: "auto" },
};

function FindMedian() {
  const generateNumbers = () => {
    const length = Math.floor(Math.random() * 4) + 4; // 4 to 7 numbers
    const nums = new Set();
    while (nums.size < length) {
      nums.add(Math.floor(Math.random() * 20) + 1);
    }
    return Array.from(nums);
  };

  const [numbers, setNumbers] = useState(generateNumbers);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [medianInput, setMedianInput] = useState("");
  const [feedback, setFeedback] = useState({ message: "", color: "" });
  const [explanation, setExplanation] = useState("");
  const [questionDone, setQuestionDone] = useState(false);

  // Drag & Drop handlers
  const onDragStart = (index) => setDraggedIndex(index);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetIndex) => {
    if (draggedIndex === null || draggedIndex === targetIndex) return;
    const newNumbers = [...numbers];
    const [draggedNum] = newNumbers.splice(draggedIndex, 1);
    newNumbers.splice(targetIndex, 0, draggedNum);
    setNumbers(newNumbers);
    setDraggedIndex(null);
  };
  const onDragEnd = () => setDraggedIndex(null);

  // Keyboard reorder
  const onKeyDown = (e, index) => {
    const newNumbers = [...numbers];
    if (e.key === "ArrowLeft" && index > 0) {
      [newNumbers[index], newNumbers[index - 1]] = [newNumbers[index - 1], newNumbers[index]];
      setNumbers(newNumbers);
    } else if (e.key === "ArrowRight" && index < numbers.length - 1) {
      [newNumbers[index], newNumbers[index + 1]] = [newNumbers[index + 1], newNumbers[index]];
      setNumbers(newNumbers);
    }
  };

  const getMedianValue = () => {
    const sorted = [...numbers].sort((a, b) => a - b);
    const len = sorted.length;
    return len % 2 === 1 ? sorted[Math.floor(len / 2)] : (sorted[len / 2 - 1] + sorted[len / 2]) / 2;
  };

  const getMedianIndices = () => {
    const sorted = [...numbers].slice().sort((a, b) => a - b);
    const len = sorted.length;
    if (len % 2 === 1) {
      const midVal = sorted[Math.floor(len / 2)];
      return numbers.map((n, i) => (n === midVal ? i : -1)).filter((i) => i !== -1);
    } else {
      const mid1 = sorted[len / 2 - 1];
      const mid2 = sorted[len / 2];
      return numbers.map((n, i) => (n === mid1 || n === mid2 ? i : -1)).filter((i) => i !== -1);
    }
  };

  const medianIndices = getMedianIndices();

  // --- Updated checkAnswer: accept exact OR rounded integer ---
  const checkAnswer = () => {
    if (medianInput.trim() === "") {
      setFeedback({ message: "Please enter your answer.", color: "#bf360c" });
      setExplanation("");
      return;
    }
    const userAnswer = parseFloat(medianInput);
    if (isNaN(userAnswer)) {
      setFeedback({ message: "Please enter a valid number.", color: "#bf360c" });
      setExplanation("");
      return;
    }

    const correctMedian = getMedianValue();
    const roundedMedian = Math.round(correctMedian);

    const isExact = Math.abs(userAnswer - correctMedian) < 0.0001;
    const isRoundedMatch = Number.isInteger(userAnswer) && userAnswer === roundedMedian;

    if (isExact || isRoundedMatch) {
      // accepted (either exact decimal like 4.7 or rounded integer like 5)
      setFeedback({ message: "🎉 Great job! That's correct!", color: "#2e7d32" });
      explainMedian(correctMedian, roundedMedian);
      setQuestionDone(true);
      ConstructorEventUpdateSQLStatus();
    } else {
      setFeedback({ message: "Oops! That's not quite right. Try again.", color: "#b71c1c" });
      setExplanation("");
    }
  };

  // Updated explanation to show exact median and rounded acceptance when needed
  const explainMedian = (correctMedian, roundedMedian) => {
    const sorted = [...numbers].slice().sort((a, b) => a - b);
    const len = sorted.length;

    if (len % 2 === 1) {
      const midVal = sorted[Math.floor(len / 2)];
      const exact = midVal;
      if (Number.isInteger(exact)) {
        setExplanation(`There are ${len} numbers. Ordered: ${sorted.join(", ")}. The median is ${exact}.`);
      } else {
        setExplanation(
          `There are ${len} numbers. Ordered: ${sorted.join(", ")}. Exact median is ${exact}. (Rounded to ${roundedMedian} — accepted.)`
        );
      }
    } else {
      const mid1 = sorted[len / 2 - 1];
      const mid2 = sorted[len / 2];
      const exact = (mid1 + mid2) / 2;
      if (Number.isInteger(exact)) {
        setExplanation(
          `There are ${len} numbers. Ordered: ${sorted.join(", ")}. The median is (${mid1} + ${mid2}) ÷ 2 = ${exact}.`
        );
      } else {
        setExplanation(
          `There are ${len} numbers. Ordered: ${sorted.join(", ")}. Exact median is ${exact}. (Rounded to ${roundedMedian} — accepted.)`
        );
      }
    }
  };

  const handleNextQuestion = () => {
    setNumbers(generateNumbers());
    setMedianInput("");
    setFeedback({ message: "", color: "" });
    setExplanation("");
    setQuestionDone(false);
  };

  return (
    <div style={styles.body}>
      <h2 style={styles.heading}>Find the Middle (Median)!</h2>
      <p style={styles.instructions}>Drag the numbers to put them in order. Then enter the middle number(s).</p>

      <div style={styles.numberContainer}>
        {numbers.map((num, idx) => (
          <div
            key={idx}
            draggable
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            onDragEnd={onDragEnd}
            onKeyDown={(e) => onKeyDown(e, idx)}
            tabIndex={0}
            style={{
              ...styles.numberBox,
              ...(draggedIndex === idx ? styles.dragging : {}),
              ...(medianIndices.includes(idx) ? styles.highlight : {}),
            }}
          >
            {num}
          </div>
        ))}
      </div>

      <div style={styles.answerSection}>
        <label>Enter the median value(s): </label>
        <br />
        <input
          type="text"
          placeholder="Type your answer here"
          value={medianInput}
          onChange={(e) => setMedianInput(e.target.value)}
          style={styles.input}
        />
        <br />
        <small>
          <em>For two middle numbers, enter their average (e.g., 5.5). If the median is decimal (e.g. 4.7), entering the rounded integer (5) is accepted.</em>
        </small>
        <br />
        <button
          onClick={checkAnswer}
          style={styles.button}
          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = styles.buttonHover.backgroundColor)}
          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = styles.button.backgroundColor)}
        >
          Check Answer
        </button>
      </div>

      <div style={{ ...styles.feedback, color: feedback.color }}>{feedback.message}</div>
      <div style={styles.explanation}>{explanation}</div>

      {questionDone && (
        <button onClick={handleNextQuestion} style={{ ...styles.button, backgroundColor: "#00796b", marginTop: 25 }}>
          ➡️ Next Question
        </button>
      )}
    </div>
  );
}

export default FindMedian;
